<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import LibraryEdit from './LibraryEdit.vue';
    import AttributeDef from './AttributeDef.vue';
    import CodeSetList from './CodeSetList.vue';
    import LibraryData from './LibraryData.vue';
    import LibraryRelations from './LibraryRelations.vue';
    import LibraryContentLog from './LibraryContentLog.vue';
    import LibraryUml from './LibraryUml.vue';
    import LibraryAttachment from './LibraryAttachment.vue';
    import LibraryStats from './LibraryStats.vue';
    import Breadcrumbs from '@/shared/components/Breadcrumbs.vue';
    import AccessControlCard from '@/shared/components/AccessControlCard.vue';
    import { explorerLinkLibrary, getSqlResultLink, getSqlEditorLinkByLibrary } from '@/shared/helpers/routing';
    import {
        getAccessPolicesForLib,
        getLibrary,
        newCodeSet,
        getVirtualLibraryOptions,
        getLibraryStatisticDetails,
        type VirtualLibraryOptionsModel,
        type CodeSetStatisticsReadModel,
        Policy
    } from '@/shared/helpers/api';
    import {
        requireCanEditLibrary as vRequireCanEditLibrary
    } from '@/shared/directives/requirePermission';
    import LibraryGroupsCard from './LibraryGroupsCard.vue';
    import LibraryDocumentationButton from './LibraryDocumentationButton.vue';
    import AddRelationLibrary from './AddRelationLibrary.vue';
    import NotFound from '@/shared/NotFound.vue';
    import LibraryChangelog from '@/apps/codeLibrary/LibraryChangelog.vue';
    import { http } from '@/shared/httpWrapper';
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import type { Library } from '@/shared/helpers/api/types/entities';
    import VueMarkdown from '@/shared/components/VueMarkdown.vue';

    const route = useRoute();
    const router = useRouter();
    const buefy = useBuefy();
    const { showError } = useShow();

    const libraryName = ref('');
    const editMode = ref(false);
    const library = ref<Library | null>(null);
    const loading = ref(true);
    const selectedTab = ref('Content');
    const policies = ref<Policy[]>([]);
    const virtualLibraryOptions = ref<VirtualLibraryOptionsModel | null>(null);
    const codeSetStatistics = ref<CodeSetStatisticsReadModel[]>([]);

    const showUnlockedForDeleteNotification = computed(() => {
        if (library.value) {
            return library.value.codeSets.some(x => !x.lockedForDelete);
        }
        return false;
    });

    const codeNameRegex = computed(() => library.value?.codeNameRegex ?? '');
    const descriptionRegex = computed(() => library.value?.descriptionRegex ?? '');

    async function fetchLibrary(name: string) {
        const [lib, pol, vlo, stats] = await Promise.all([
            getLibrary(http, name),
            getAccessPolicesForLib(http, name)
                .catch(() => [] as Policy[]),
            getVirtualLibraryOptions(http, name),
            getLibraryStatisticDetails(http, name, false).catch(() => [] as CodeSetStatisticsReadModel[])
        ]);

        library.value = lib ? (lib as Library) : null;
        policies.value = pol;
        virtualLibraryOptions.value = vlo;
        codeSetStatistics.value = stats;
        loading.value = false;
    }

    // Saving recomputes the item counts on the server, so only the statistics need to be read again
    async function refreshStatistics() {
        codeSetStatistics.value = await getLibraryStatisticDetails(http, libraryName.value, false)
            .catch(() => codeSetStatistics.value);
    }

    function addCodeSet() {
        buefy.dialog.prompt({
            message: 'New library set:',
            inputAttrs: {
                placeholder: 'Set description',
                minlength: 1,
                maxlength: 256,
                required: true
            },
            onConfirm: async (description: string) => {
                try {
                    await newCodeSet(http, library.value!.name, description);
                    await fetchLibrary(library.value!.name);
                } catch {
                    showError('Cannot Create Set');
                }
            }
        });
    }

    function editLibrary() {
        editMode.value = true;
    }

    async function editDone() {
        editMode.value = false;
        await fetchLibrary(libraryName.value);
    }

    function queryLibrary() {
        router.push(getSqlEditorLinkByLibrary(libraryName.value));
    }

    function exploreLibrary() {
        router.push(explorerLinkLibrary(libraryName.value));
    }

    function gotoVirtualCodeResult() {
        router.push(getSqlResultLink(
            `SELECT * FROM ${library.value!.name}`,
            library.value!.name
        ));
    }

    onMounted(async () => {
        libraryName.value = decodeURIComponent(route.params.libraryName as string);

        try {
            await fetchLibrary(libraryName.value);
        } catch {
            loading.value = false;
        }
    });
</script>

<template>
    <div>
        <b-loading
            :is-full-page="false"
            :model-value="loading" />
        <breadcrumbs
            :library="library" />
        <b-notification
            v-if="showUnlockedForDeleteNotification"
            type="is-warning"
            :closable="false"
            has-icon
            role="alert">
            <p><b>Items may be deleted</b></p>
            <p v-if="library && library.codeSets.length === 1">
                This library is opened so that items may be deleted. This generally means the library is <u>not production ready</u>, and it must be locked for deletion when appropriate.
            </p>
            <p v-else>
                One or more scoped library sets are open so that items may be deleted. This generally means the set is <u>not production ready</u>, and it must be locked for deletion when appropriate.
            </p>
        </b-notification>
        <not-found
            v-if="!library && !loading"
            icon="magnify-remove-outline"
            link-to="/Library"
            link-label="Go to Library List">
            Library <strong> {{ libraryName }} </strong> could not be found
        </not-found>

        <div v-if="!editMode && library">
            <div class="columns">
                <div class="column">
                    <h1 class="title">
                        {{ library.name }}
                    </h1>
                </div>
                <div class="column">
                    <b-field position="is-right">
                        <p class="control">
                            <library-documentation-button
                                :library-name="libraryName"
                                button-type="is-default" />
                        </p>
                        <p class="control">
                            <button
                                class="button"
                                @click="exploreLibrary">
                                <b-icon
                                    icon="graph"
                                    size="is-small" />
                                <span>Explore</span>
                            </button>
                        </p>
                        <p class="control">
                            <button
                                class="button"
                                @click="queryLibrary">
                                <b-icon
                                    icon="magnify"
                                    size="is-small" />
                                <span>Query</span>
                            </button>
                        </p>
                        <p class="control">
                            <add-relation-library
                                :library-name="libraryName" />
                        </p>
                        <p
                            v-show="!library.isGlobal "
                            v-require-can-edit-library="library.name"
                            class="control">
                            <button
                                class="button is-info"
                                @click="addCodeSet">
                                <b-icon
                                    icon="plus"
                                    size="is-small" />
                                <span>New set</span>
                            </button>
                        </p>
                        <p
                            v-require-can-edit-library="library.name"
                            class="control">
                            <button
                                class="button is-primary"
                                @click="editLibrary">
                                <b-icon
                                    icon="pencil"
                                    size="is-small" />
                                <span>Edit Library</span>
                            </button>
                        </p>
                    </b-field>
                </div>
            </div>
            <div class="columns is-desktop is-multiline">
                <div class="column is-one-quarter-desktop is-full-tablet is-full-mobile">
                    <library-data
                        :library="library" />
                    <library-attachment
                        v-if="library"
                        :attachment="library.attachment"
                        :library-name="library.name"
                        @updateAttachment="editDone" />
                    <library-groups-card
                        :tags="library.tags" />
                    <access-control-card
                        :access-groups="library.accessGroups"
                        :policies="policies"
                        :card-title="'Access Control'"
                        :library-access-control="true" />
                </div>
                <div class="column">
                    <b-tabs
                        v-model="selectedTab"
                        size="is-medium"
                        type="is-boxed"
                        class="block">
                        <b-tab-item
                            label="Content"
                            value="Content">
                            <section v-if="library.isVirtual">
                                <div v-if="virtualLibraryOptions">
                                    <b-notification
                                        v-show="virtualLibraryOptions.compilerError"
                                        type="is-danger"
                                        :closable="false"
                                        has-icon
                                        role="alert">
                                        <b>Query issue</b><br>
                                        {{ virtualLibraryOptions.compilerError }}
                                    </b-notification>
                                    <b-notification
                                        v-show="virtualLibraryOptions.compilerWarning"
                                        type="is-warning"
                                        :closable="false"
                                        has-icon
                                        role="alert">
                                        <b>Potential query issue</b><br>
                                        {{ virtualLibraryOptions.compilerWarning }}
                                    </b-notification>
                                </div>
                                <div
                                    style="text-align:center;padding-top:10em;">
                                    <b-button
                                        type="is-info"
                                        size="is-large"
                                        @click="gotoVirtualCodeResult">
                                        Open content view
                                    </b-button>
                                    <p style="padding-top: 1em;">
                                        Virtual library content is viewed on a separate page.
                                    </p>
                                </div>
                            </section>
                            <code-set-list
                                v-else
                                id="codeset-list"
                                :library="library"
                                :statistics="codeSetStatistics"
                                @refresh="refreshStatistics" />
                        </b-tab-item>
                        <b-tab-item
                            label="Definition"
                            value="Attributes">
                            <div
                                v-if="library.definition"
                                class="content mb-5">
                                <vue-markdown
                                    :source="library.definition"
                                    :html-enabled="false" />
                            </div>
                            <h2 class="title is-5">
                                Attribute definitions
                            </h2>
                            <attribute-def
                                id="attribute-def"
                                :library-name="libraryName"
                                :code-name-regex="codeNameRegex"
                                :description-regex="descriptionRegex"
                                :name-in-identity="library.nameInIdentity"
                                :description-is-required="library.descriptionIsRequired"
                                :name-alias="library.nameAlias"
                                :is-relation="library.isRelation" />
                        </b-tab-item>
                        <b-tab-item
                            label="UML"
                            value="Relations">
                            <keep-alive>
                                <library-uml
                                    v-if="selectedTab === 'Relations'"
                                    :library-name="libraryName" />
                            </keep-alive>
                        </b-tab-item>
                        <b-tab-item
                            label="Dependencies"
                            value="Dependencies">
                            <library-relations
                                v-if="selectedTab === 'Dependencies'"
                                :library="library" />
                        </b-tab-item>
                        <b-tab-item
                            label="Content log"
                            value="Content changelog">
                            <library-content-log
                                v-if="selectedTab === 'Content changelog'"
                                :library-name="library.name" />
                        </b-tab-item>
                        <b-tab-item
                            label="Library log"
                            value="Library changelog">
                            <library-changelog
                                v-if="selectedTab === 'Library changelog'"
                                :library-name="library.name" />
                        </b-tab-item>
                        <b-tab-item
                            label="Usage statistics"
                            value="Statistics">
                            <library-stats
                                v-if="selectedTab === 'Statistics'"
                                :library-name="library.name" />
                        </b-tab-item>
                    </b-tabs>
                </div>
            </div>
        </div>
        <library-edit
            v-if="editMode"
            :library-name="libraryName"
            @done="editDone" />
    </div>
</template>
